import { acquisitionApiRequest, AcquisitionInitResponse } from '../../utils/acquisition-api/AcquisitionApi';
import SessionStorage, { clearAquisitionData } from '../../utils/SessionStorage';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { flowDataModule } from '../../utils/flowDataModule';
import { AcquisitionFlow, getAcquisitionFlow, getMarketingAttributesForAcquisitions } from '../../utils/acquisition-utils';
import { IPreApprovedLookupResponse } from '../../types/responses/PreApprovedLookup';
import { cleanInternalLinkUrl } from '../../utils/cob-utils';

export const initSkipPqApp = async () => {
    const isAuthor = window.location.port === '4502';

    if (isAuthor) {
        return;
    }

    const data = JSON.parse(PathUtils.getMetaPropertyValue('skipPqParamsURL') as string);
    if (data?.redirectUrl) {
        window.location.replace(data.redirectUrl);
        return;
    }

    if (
        !SessionStorage.getObject('adobeMode') &&
        !SessionStorage.verifyNotEmpty('initResponse')
    ) {
        const response = await acquisitionApiRequest<AcquisitionInitResponse>('init-compare-api', {}).catch(() => {
            // if there's an error with initializing, handle it the same way as failover mode
            return { data: { SPMaintenanceMode: true } };
        });

        if (response?.data?.SPMaintenanceMode) {
            window.location.assign('/full-application/data-entry/updating-systems');
            return false;
        }
        SessionStorage.setObject('initResponse', response.data);
    }
    const response = await acquisitionApiRequest<any>(
        'skip-pq-get-application-info',
        {
            method: 'POST',
            body: {
                ProgramCode:
                getAcquisitionFlow() === (AcquisitionFlow.PqOfOne || AcquisitionFlow.PHA) ?  SessionStorage.get('selectedProgramCode') : PathUtils.getMetaPropertyValue('skipPqProgramCode'),
            },
        },
    );
    const responseData = response.data;

    if (!responseData || responseData?.Result.CardDesign.Cards.length === 0 || !responseData?.Result.ProgramRates.ProgramCode) {
        window.location.assign(cleanInternalLinkUrl('/offer-unavailable'));
    }

    const transformResponse = {
        ApplicationInformation: [responseData.Result],
    }; 
    await flowDataModule.setInSession({
        IsAuthorizedUserEligible:
        responseData.Result.AuthorizedUserEligible,
        IsCounterOffer: responseData.Result.IsCounterOffer,
        Cards: responseData.Result.CardDesign.Cards,
    });
    await SessionStorage.setCipherWithCurrentScope('prequalResponse', transformResponse);
    SessionStorage.set(
        'selectedProgramCode',
        responseData.Result.ProgramRates.ProgramCode,
    );
    SessionStorage.setObject(
        'cardInfo',
        responseData.Result.CardDesign.Cards,
    );

    return true;
};

export const initSkipPaApp = async (data?:any) => {
    const requiredParams = ['C1BSourceID', 'ApcID', 'zip'];
    const params = new URLSearchParams(window.location.search);
    for (const param of requiredParams) {
        if (!params.has(param)) {
            throw new Error(`Missing required param: ${param}`);
        }
    }

    const requestBody = {
        ApprovalCode: params.get('ApcID'),
        LookupData: params.get('zip'),
        LookupDataType: 2, // SSN: 0, LAST_NAME: 1, ZIP_CODE: 2, SSN_LAST_FOUR_DIGITS: 3
        MarketingAttributes: getMarketingAttributesForAcquisitions(),
    };

    const errorCodes = (await flowDataModule.getFromSession() as any).ErrorCodes; // any used to address legacy typing issues

    const handleResponse = async (response: {status: number, data: IPreApprovedLookupResponse}) => {
        switch (response.data.Result) {
            case 0: // UnableToFindMatch
            case 6: // InvalidLookupDataType
            case 7: // InvalidLookupData
                throw {
                    errCode: errorCodes.nomatch,
                    message: 'Unable to find match',
                };
            case 1: //ExceededAttempts
                throw {
                    errCode: errorCodes.exceededattempts,
                    message: 'Exceeded attempts',
                };
            case 2: //AlreadyInProgress
                throw {
                    errCode: errorCodes.inprogress,
                    message: 'App is already in progress',
                };
            case 4: //InvalidCodeOrSsn
                throw {
                    errCode: errorCodes.unrecognizedField,
                    message: 'Invalid data sent',
                };
            case 5: //ExceedetAttemptsInSession
                throw {
                    errCode: errorCodes.exceededAttemptsInSession,
                    message: 'Exceeded attempts in session',
                };
            case 9: //Expired offer
                throw {
                    errCode: errorCodes.processingExpiredOffer,
                    message: 'Offer was expired',
                };
            case 3: //Successful
                await flowDataModule.setInSession({
                    ...response.data.AdditionalInformation,
                });
                if (response.data.Applicant && response.data.CardDesign && response.data.CardDesign.Cards) {
                    SessionStorage.setObject('cardInfo', response.data.CardDesign.Cards);
                }
                try {
                    await SessionStorage.setCipherWithCurrentScope('pre-approval-data', response.data);
                } catch (e) {
                    console.log(e);
                }
                SessionStorage.set('selectedProgramCode', response.data.ReservationNumber.substring(0, 4));
                SessionStorage.set('approvalCode', response.data.ReservationNumber);
                break;
            default:
                throw {
                    errCode: errorCodes.default,
                    message: 'Unhandled response enum was returned',
                };
        }
    };

    try {
        const response = await acquisitionApiRequest<IPreApprovedLookupResponse>(
            'pre-approved-lookup',
            {
                method: 'post',
                body: requestBody,
            },
            data,
        );
        if (response.data.Result === undefined
            || response.data.Result === null
            || response.status !== 200) {
            throw {
                response: response,
            };
        } else {
            await handleResponse(response);
        }
    } catch (error) {
        if ((error as any)?.response?.status === 408) {
            clearAquisitionData();
            throw {
                errCode: errorCodes.serverConnectionLostErrorCode,
                message: 'Server session expired',
            };
        } else if ((error as any)?.response?.data?.Result !== undefined && (error as any).response.data.Result !== null) {
            await handleResponse((error as any).response);
        } else {
            throw {
                errCode: errorCodes.serverConnectionLostErrorCode,
                message: 'Unhandled error occurred',
            };
        }
    }
};